<template>
    <div>
        <button
            type="button"
            class="rounded-2xl z-[2] sticky top-4 border border-solid border-white shadow-lg flex w-full items-center justify-between gap-2 px-4 py-3 text-start bg-white/50 backdrop-blur-sm cursor-pointer border-t-0 hover:bg-white/80 transition"
            :class="{ 'animate-section-blink': isHighlighted }"
            @click="sectionExpanded = !sectionExpanded"
        >
            <div class="flex justify-between grow items-center gap-2">
                <span class="text-sm font-bold text-slate-700">{{ props.section.label }}</span>
                <vx-badge :color="badgeColor" size="xs">
                    {{ props.section.categories.length }}
                </vx-badge>
            </div>
            <font-awesome-icon
                widthAuto
                class="text-xs text-slate-400 transition-transform"
                :class="{ 'rotate-180': sectionExpanded }"
                :icon="faChevronDown"
            ></font-awesome-icon>
        </button>
        <div v-if="sectionExpanded" class="pt-4 pb-6 flex flex-col gap-2">
            <intelligent-inbox-category
                v-for="category in props.section.categories"
                :key="category.id"
                :category="category"
                :threads="props.threads[category.id] ?? []"
                :expanded="!!categoryExpanded[category.id]"
                :highlighted="isHighlighted"
                @toggle="toggleCategory(category.id)"
            ></intelligent-inbox-category>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge } from '@voxie/frontend-components';
import type { VxBadgeColor } from '@voxie/frontend-components';
import { computed, reactive } from 'vue';
import type { IntelligentSection, IntelligentThread } from './useIntelligentInbox';
import { useIntelligentInbox } from './useIntelligentInbox';
import IntelligentInboxCategory from './IntelligentInboxCategory.vue';

const props = defineProps<{
    section: IntelligentSection;
    threads: Record<string, IntelligentThread[]>;
}>();

const { expandedSections, highlightedSection } = useIntelligentInbox();

const isHighlighted = computed(() => highlightedSection.value === props.section.state);

const sectionExpanded = computed({
    get: () => !!expandedSections.value[props.section.state],
    set: (val: boolean) => {
        expandedSections.value[props.section.state] = val;
    },
});
const categoryExpanded = reactive<Record<string, boolean>>({});

const toggleCategory = (id: string): void => {
    categoryExpanded[id] = !categoryExpanded[id];
};

const badgeColor = computed<VxBadgeColor>(() => {
    return props.section.color === 'emerald' ? 'success' : (props.section.color as VxBadgeColor);
});
</script>

<style scoped>
@keyframes section-blink {
    0%,
    100% {
        background-color: rgb(255 255 255 / 0.5);
    }
    50% {
        background-color: rgb(255 255 255 / 1);
    }
}
.animate-section-blink {
    animation: section-blink 0.6s ease-in-out 2;
}
</style>
